// src/hooks/api/useGameweeks.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type {
  Gameweek,
  CreateGameweekRequest,
  UpdateGameweekRequest,
} from "@/types/api.types";

const BASE = "/api/v1/admin/gameweeks";

const gameweekKeys = {
  all: ["gameweeks"] as const,
  list: () => [...gameweekKeys.all, "list"] as const,
  stats: (id: number) => [...gameweekKeys.all, "stats", id] as const,
  totw: (id: number, formation?: string) =>
    [...gameweekKeys.all, "totw", id, formation ?? "default"] as const,
  eligiblePlayers: (id: number) =>
    [...gameweekKeys.all, "eligible-players", id] as const,
  tournamentTotw: (formation?: string, phase?: string) =>
    [
      ...gameweekKeys.all,
      "totw-tournament",
      formation ?? "default",
      phase ?? "ALL",
    ] as const,
  tournamentEligiblePlayers: (phase?: string) =>
    [...gameweekKeys.all, "eligible-players-tournament", phase ?? "ALL"] as const,
};

/**
 * Knockout/group stages the Team of the Tournament can be narrowed to.
 * "ALL" spans every match played and is the default.
 */
export const TOTW_PHASES = [
  "ALL",
  "GROUP_STAGE",
  "ROUND_OF_32",
  "ROUND_OF_16",
  "QUARTER_FINAL",
  "SEMI_FINAL",
  "THIRD_PLACE",
  "FINAL",
] as const;
export type TotwPhase = (typeof TOTW_PHASES)[number];

export const TOTW_PHASE_LABEL: Record<TotwPhase, string> = {
  ALL: "Whole tournament",
  GROUP_STAGE: "Group stage",
  ROUND_OF_32: "Round of 32",
  ROUND_OF_16: "Round of 16",
  QUARTER_FINAL: "Quarter finals",
  SEMI_FINAL: "Semi finals",
  THIRD_PLACE: "Third place play-off",
  FINAL: "Final",
};

export const TOTW_FORMATIONS = [
  "4-4-2",
  "4-3-3",
  "3-4-3",
  "3-5-2",
  "4-2-3-1",
  "5-3-2",
  "5-4-1",
] as const;
export type TotwFormation = (typeof TOTW_FORMATIONS)[number];

export type TotwSlot = {
  slot: { position: "GK" | "DEF" | "MID" | "FWD"; index: number };
  isCaptain: boolean;
  player: {
    id: number;
    name: string;
    photo: string | null;
    position: "GK" | "DEF" | "MID" | "FWD";
    nation: { id: number; name: string; flagUrl: string | null } | null;
    club: { id: number; name: string; logoUrl: string | null } | null;
  };
  stats: {
    rawPoints: number;
    appearances: number;
    longAppearances: number;
    goals: number;
    assists: number;
    cleanSheets: number;
    yellowCards: number;
    redCards: number;
    penaltySaved: number;
    penaltyMissed: number;
    ownGoals: number;
  };
};

export type TotwResponse = {
  gameweek: { id: number; name: string; status: string };
  formation: string;
  players: TotwSlot[];
  totalPoints: number;
};

export function useGameweekTotw(id: number | null, formation?: TotwFormation) {
  return useQuery({
    queryKey: id
      ? gameweekKeys.totw(id, formation)
      : ["gameweeks", "totw", "none", formation ?? "default"],
    queryFn: async (): Promise<TotwResponse> => {
      const qs = formation ? `?formation=${encodeURIComponent(formation)}` : "";
      return apiClient.get(`${BASE}/${id}/team-of-the-week${qs}`);
    },
    enabled: !!id,
  });
}

export type EligiblePlayer = {
  player: TotwSlot["player"];
  position: "GK" | "DEF" | "MID" | "FWD";
  stats: TotwSlot["stats"];
};

export type EligiblePlayersResponse = {
  players: EligiblePlayer[];
};

/**
 * All players who appeared in this gameweek, sorted by points.
 * Used by the TOTW page to power the per-slot swap picker.
 */
export function useGameweekEligiblePlayers(id: number | null) {
  return useQuery({
    queryKey: id
      ? gameweekKeys.eligiblePlayers(id)
      : ["gameweeks", "eligible-players", "none"],
    queryFn: async (): Promise<EligiblePlayersResponse> =>
      apiClient.get(`${BASE}/${id}/eligible-players`),
    enabled: !!id,
  });
}

/**
 * Team of the Tournament — same shape as TotwResponse but scoped to the
 * whole competition, so `gameweek` is replaced by a `scope` block.
 */
export type TournamentTotwResponse = {
  scope: {
    kind: "TOURNAMENT";
    phase: string | null;
    matchCount: number;
    playerCount: number;
  };
  formation: string;
  players: TotwSlot[];
  totalPoints: number;
};

export function useTournamentTotw(
  formation?: TotwFormation,
  phase?: TotwPhase,
  enabled = true,
) {
  return useQuery({
    queryKey: gameweekKeys.tournamentTotw(formation, phase),
    queryFn: async (): Promise<TournamentTotwResponse> => {
      const params = new URLSearchParams();
      if (formation) params.set("formation", formation);
      if (phase && phase !== "ALL") params.set("phase", phase);
      const qs = params.toString();
      return apiClient.get(
        `${BASE}/team-of-the-tournament${qs ? `?${qs}` : ""}`,
      );
    },
    enabled,
  });
}

export function useTournamentEligiblePlayers(
  phase?: TotwPhase,
  enabled = true,
) {
  return useQuery({
    queryKey: gameweekKeys.tournamentEligiblePlayers(phase),
    queryFn: async (): Promise<EligiblePlayersResponse> => {
      const qs = phase && phase !== "ALL" ? `?phase=${phase}` : "";
      return apiClient.get(`${BASE}/tournament-eligible-players${qs}`);
    },
    enabled,
  });
}

export function useGameweeks() {
  return useQuery({
    queryKey: gameweekKeys.list(),
    queryFn: async (): Promise<Gameweek[]> => apiClient.get(BASE),
    refetchInterval: 45000,
  });
}

export function useGameweekStats(id: number) {
  return useQuery({
    queryKey: gameweekKeys.stats(id),
    queryFn: async () => apiClient.get(`${BASE}/${id}/stats`),
    enabled: !!id,
  });
}

export function useCreateGameweek() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateGameweekRequest) => apiClient.post(BASE, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: gameweekKeys.all }),
  });
}

export function useUpdateGameweek() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateGameweekRequest }) =>
      apiClient.patch(`${BASE}/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: gameweekKeys.all }),
  });
}

export function useOpenGameweek() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiClient.post(`${BASE}/${id}/open`),
    onSuccess: () => qc.invalidateQueries({ queryKey: gameweekKeys.all }),
  });
}

export function useLockGameweek() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiClient.post(`${BASE}/${id}/lock`),
    onSuccess: () => qc.invalidateQueries({ queryKey: gameweekKeys.all }),
  });
}

export function useCalculateGameweekPoints() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiClient.post(`${BASE}/${id}/calculate-points`),
    onSuccess: () => qc.invalidateQueries({ queryKey: gameweekKeys.all }),
  });
}

export function useDeleteGameweek() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiClient.delete(`${BASE}/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: gameweekKeys.all }),
  });
}

export function useAssignMatches() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, matchIds }: { id: number; matchIds: number[] }) =>
      apiClient.post(`${BASE}/${id}/assign-matches`, { matchIds }),
    onSuccess: () => qc.invalidateQueries({ queryKey: gameweekKeys.all }),
  });
}

export function useForceRecalculate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiClient.post(`${BASE}/${id}/force-recalculate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: gameweekKeys.all }),
  });
}

export function useResyncTeams() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiClient.post(`${BASE}/${id}/resync-teams`),
    onSuccess: () => qc.invalidateQueries({ queryKey: gameweekKeys.all }),
  });
}
